import { useId, useState } from 'react'

import type { Activity } from '@fit-editor/fit-core'

import { AllMessagesPanel } from '@/components/data/all-messages-panel'
import { FieldCellRendererContext } from '@/components/data/field-cell-renderer'
import type { FieldCellRenderer } from '@/components/data/field-cell-renderer'
import { FileInfoPanel } from '@/components/data/file-info-panel'
import { MessageTable } from '@/components/data/message-table'
import { RecordsTable } from '@/components/data/records-table'
import { useFitDataModel, useFitProfile } from '@/components/data/use-fit-data-model'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useCurrentActivity } from '@/hooks/use-current-activity'
import { cn } from '@/lib/utils'
import { useActivityStore } from '@/store/activity-store'
import type { LoadedActivity } from '@/store/activity-store'

const SESSION_COLUMNS = [
  'sport',
  'subSport',
  'startTime',
  'timestamp',
  'totalElapsedTime',
  'totalTimerTime',
  'totalMovingTime',
  'totalDistance',
  'totalAscent',
  'totalDescent',
  'avgHeartRate',
  'maxHeartRate',
  'enhancedAvgSpeed',
  'avgSpeed',
  'enhancedMaxSpeed',
  'maxSpeed',
  'avgCadence',
  'maxCadence',
  'avgPower',
  'maxPower',
  'totalCalories',
  'numLaps',
] as const

const LAP_COLUMNS = [
  'startTime',
  'timestamp',
  'totalElapsedTime',
  'totalTimerTime',
  'totalMovingTime',
  'totalDistance',
  'totalAscent',
  'totalDescent',
  'avgHeartRate',
  'maxHeartRate',
  'enhancedAvgSpeed',
  'avgSpeed',
  'enhancedMaxSpeed',
  'maxSpeed',
  'avgCadence',
  'maxCadence',
  'avgPower',
  'maxPower',
  'totalCalories',
  'lapTrigger',
  'sport',
] as const

const DEVICE_COLUMNS = [
  'deviceIndex',
  'deviceType',
  'antplusDeviceType',
  'bleDeviceType',
  'localDeviceType',
  'sourceType',
  'manufacturer',
  'garminProduct',
  'product',
  'productName',
  'serialNumber',
  'softwareVersion',
  'hardwareVersion',
  'batteryStatus',
  'batteryVoltage',
  'batteryLevel',
  'cumOperatingTime',
  'antDeviceNumber',
  'antNetwork',
  'descriptor',
  'timestamp',
] as const

export type FitDataTab = 'file' | 'sessions' | 'laps' | 'devices' | 'records' | 'messages'

export interface FitDataViewProps {
  loaded: LoadedActivity
  /** Field-editing extension point -- see `field-cell-renderer.ts`. */
  renderFieldCell?: FieldCellRenderer
}

/**
 * The "FIT data" workspace view: an inspector over the selected
 * activity's FIT messages -- by default the *current* (edited) state,
 * replayed in the Worker, or, via the toggle, the unedited original
 * snapshot (`history.original`, already on the main thread).
 */
export function FitDataView({ loaded, renderFieldCell }: FitDataViewProps) {
  const [showOriginal, setShowOriginal] = useState(false)
  const { result } = useCurrentActivity(showOriginal ? undefined : loaded.history, {
    keepPreviousWhileLoading: true,
  })
  const activity: Activity | undefined = showOriginal ? loaded.history.original : result?.activity

  const editCount = loaded.history.cursor
  const toggleId = useId()

  return (
    <section aria-label="FIT data" className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <p className="text-sm" aria-live="polite">
          <span className="text-muted-foreground">Showing: </span>
          {showOriginal ? (
            <span className="text-warning-text font-medium">
              Original file (unedited, as imported)
            </span>
          ) : (
            <span className="font-medium">
              Current state
              {editCount > 0
                ? ` (after ${editCount} edit${editCount === 1 ? '' : 's'})`
                : ' (no edits applied — identical to the original)'}
            </span>
          )}
        </p>
        <label htmlFor={toggleId} className="flex items-center gap-1.5 text-sm">
          <input
            id={toggleId}
            type="checkbox"
            checked={showOriginal}
            onChange={(e) => setShowOriginal(e.target.checked)}
            className="accent-primary"
          />
          Show original file
        </label>
      </div>

      {activity == null ? (
        <p className="text-muted-foreground text-sm">Loading &quot;{loaded.fileName}&quot;…</p>
      ) : (
        <FieldCellRendererContext.Provider value={renderFieldCell ?? null}>
          <FitDataTabs activity={activity} source={showOriginal ? 'original' : 'current'} />
        </FieldCellRendererContext.Provider>
      )}
    </section>
  )
}

function FitDataTabs({ activity, source }: { activity: Activity; source: 'current' | 'original' }) {
  const [tab, setTab] = useState<FitDataTab>('file')
  const timeMode = useActivityStore((s) => s.ui.timeDisplayMode)
  const { profile, error } = useFitProfile()
  const model = useFitDataModel(activity, profile)
  const deviceCount = model.byKey.get('deviceInfoMesgs')?.length ?? 0

  return (
    <div
      className={cn(
        'flex flex-col',
        source === 'original' && 'border-warning/60 rounded-lg border-2 border-dashed p-2',
      )}
    >
      {error && (
        <p className="text-warning-text mb-2 text-xs" role="status">
          Couldn&apos;t load FIT Profile field metadata; units and raw values are unavailable.
        </p>
      )}
      <Tabs value={tab} onValueChange={(v) => setTab(v as FitDataTab)}>
        <TabsList
          aria-label="FIT data sections"
          className="max-sm:overflow-x-auto max-sm:overflow-y-hidden max-sm:*:shrink-0 max-sm:*:whitespace-nowrap sm:flex-wrap"
        >
          <TabsTrigger value="file">File info</TabsTrigger>
          <TabsTrigger value="sessions">Sessions ({activity.sessions.length})</TabsTrigger>
          <TabsTrigger value="laps">Laps ({activity.laps.length})</TabsTrigger>
          <TabsTrigger value="devices">Devices ({deviceCount})</TabsTrigger>
          <TabsTrigger value="records">
            Records ({activity.records.length.toLocaleString()})
          </TabsTrigger>
          <TabsTrigger value="messages">All messages</TabsTrigger>
        </TabsList>
        <TabsContent value="file">
          <FileInfoPanel model={model} timeMode={timeMode} />
        </TabsContent>
        <TabsContent value="sessions">
          <MessageTable
            model={model}
            messagesKey="sessionMesgs"
            caption="Session messages"
            rowLabel="Session"
            preferredOrder={SESSION_COLUMNS}
            source={source}
            timeMode={timeMode}
            emptyText="This file has no session messages."
          />
        </TabsContent>
        <TabsContent value="laps">
          <MessageTable
            model={model}
            messagesKey="lapMesgs"
            caption="Lap messages"
            rowLabel="Lap"
            preferredOrder={LAP_COLUMNS}
            source={source}
            timeMode={timeMode}
            emptyText="This file has no lap messages."
          />
        </TabsContent>
        <TabsContent value="devices">
          <MessageTable
            model={model}
            messagesKey="deviceInfoMesgs"
            caption="Devices and sensors (device_info messages)"
            rowLabel="Device"
            preferredOrder={DEVICE_COLUMNS}
            source={source}
            timeMode={timeMode}
            emptyText="This file has no device_info messages."
          />
        </TabsContent>
        <TabsContent value="records">
          <RecordsTable model={model} source={source} timeMode={timeMode} />
        </TabsContent>
        <TabsContent value="messages">
          <AllMessagesPanel model={model} source={source} timeMode={timeMode} />
        </TabsContent>
      </Tabs>
    </div>
  )
}
